import React, { FC, useContext } from 'react';
import cx from 'classnames';

import useThemeBlock from '@/hooks/useThemeBlock';
import { CardLinkProps } from '@/helpers/CardControls/typings';
import { Image } from '@/components/molecules/Image/render';
import { Link } from '@/components/atoms/Link/render';
import { LocaleContext } from '@/contexts/locale-context';

import block from './block.json';
import { getStyles } from './styles.css';

/**
 * TYPINGS
 */
export type CardArtistType = {
	id: number;
	image: ImageProps;
	name: string;
	role?: string;
	link?: CardLinkProps;
	showBio?: Boolean;
	desc?: string;
};
export type CardArtistProps = CardArtistType & ThemeProps;

/**
 * COMPONENT
 */
export const CardArtist: FC<CardArtistProps> & BlockConfigs = ({
	name,
	role,
	image,
	showBio = false,
	desc,
	link = {},
	theme = {},
}) => {
	const { variant, blockTheme } = useThemeBlock(block.slug, theme);

	const { __t } = useContext(LocaleContext);

	const rootClass = cx(
		'supt-card supt-cardArtist',
		showBio && 'supt-cardArtist--bio',
		getStyles(blockTheme),
		link?.href && 'supt-withLink'
	);

	// if image ends up being larger than the container, we need to correct the width in "sizes" to fetch a bigger image
	// (e.g. a 2:1 image needs to be 200vw if taking full screen width in a square container, since half of the image will take full width)
	const imageRatio = image ? image?.width / image?.height : 1;
	const containerRatio = 1 / 1; // square
	const ratioCorrection =
		imageRatio > containerRatio ? imageRatio / containerRatio : 1;

	return (
		<div
			className={rootClass}
			data-block={block.slug}
			data-variant={variant}
		>
			{image?.src && (
				<div className="supt-cardArtist__imageWrapper">
					<Image
						sizes={`(max-width: 767px) ${
							ratioCorrection * 100
						}vw, (max-width: 899px) ${
							ratioCorrection * 50
						}vw, (max-width: 1199px) ${
							ratioCorrection * 30
						}vw, (max-width: 1679px) ${
							ratioCorrection * 20
						}vw, ${Math.round(ratioCorrection * 375)}px`}
						{...image}
						className="supt-cardArtist__image"
					/>
				</div>
			)}

			{showBio ? (
				<div className="supt-cardArtist__content">
					<h3
						className="supt-cardArtist__name"
						dangerouslySetInnerHTML={{ __html: name }}
					/>
					{desc && (
						<p
							className="supt-cardArtist__bio"
							dangerouslySetInnerHTML={{ __html: desc }}
						/>
					)}
					{link?.href ? (
						<Link
							href={link?.href}
							{...(link?.opensInNewTab && {
								target: '_blank',
								rel: 'noopener noreferrer',
							})}
							className="supt-cardArtist__link"
						>
							<span>
								{__t('card-artist-link', 'En savoir plus')}
							</span>
						</Link>
					) : null}
				</div>
			) : (
				<>
					{link?.href ? (
						<Link
							href={link?.href}
							{...(link?.opensInNewTab && {
								target: '_blank',
								rel: 'noopener noreferrer',
							})}
							className="supt-cardArtist__link"
						>
							<h3
								className="supt-cardArtist__name"
								dangerouslySetInnerHTML={{ __html: name }}
							/>
						</Link>
					) : (
						<h3
							className="supt-cardArtist__name"
							dangerouslySetInnerHTML={{ __html: name }}
						/>
					)}
					{role && (
						<p
							className="supt-cardArtist__role"
							dangerouslySetInnerHTML={{ __html: role }}
						/>
					)}
				</>
			)}
		</div>
	);
};

CardArtist.slug = block.slug;
CardArtist.title = block.title;
